import { useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChecklistDialog, LrDialog, TransitDialog, DeliverDialog, CancelDialog } from "./ShipmentDialogs";
import { WhatsAppShare } from "./WhatsAppShare";

export function ShipmentOps({ shipment: s, canDispatch, canSales, onChanged }) {
  const { user } = useAuth();
  const [checkOpen, setCheckOpen] = useState(false);
  const [lrOpen, setLrOpen] = useState(false);
  const [transitOpen, setTransitOpen] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const [confirming, setConfirming] = useState(false);

  const dispatchNow = async (overrideReason = null) => {
    if (confirming) return;
    setConfirming(true);
    try {
      await api.post(`/shipments/${s.shipment_id}/dispatch`, typeof overrideReason === "string" && overrideReason ? { override_reason: overrideReason } : {});
      toast.success("Dispatch confirmed successfully.");
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "PACKED_STOCK_INSUFFICIENT" && ["admin", "warehouse_manager"].includes(user?.role)) {
        const reason = window.prompt(`${d.message}\n\nManager override — enter mandatory reason:`);
        setConfirming(false);
        if (reason && reason.trim()) return dispatchNow(reason.trim());
        toast.error("Dispatch blocked — packed stock insufficient");
        return;
      }
      if (d?.code === "ALREADY_DISPATCHED" || d?.code === "DISPATCH_IN_PROGRESS") {
        toast.info(d.message);
        onChanged?.();
        return;
      }
      if (e.response?.status === 403) { toast.error("Permission denied — you are not authorised to confirm dispatch."); return; }
      if (e.response?.status === 401) { toast.error("Your session has expired — please sign in again."); return; }
      if (typeof d === "string") { toast.error(d); return; }
      if (d?.message) { toast.error(d.message); return; }
      if (Array.isArray(d)) { toast.error(`Dispatch confirmation failed: invalid request (${d[0]?.msg || "validation error"})`); return; }
      toast.error(e.response ? `Dispatch confirmation failed (HTTP ${e.response.status}).` : `Dispatch confirmation failed: ${e.message || "network error"}`);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid={`shipment-ops-${s.shipment_id}`}>
      {["created", "loading"].includes(s.status) && (
        <span className="text-xs text-muted-foreground self-center">Loading is a tracked task — see Tasks page ({s.status === "created" ? "assigned" : "in progress"})</span>)}
      {["created", "loading", "loaded"].includes(s.status) && canDispatch && (
        <Button size="sm" variant="ghost" className="text-signal-red" onClick={() => setCancelOpen(true)} data-testid={`shipment-cancel-${s.shipment_id}`}>Cancel Shipment</Button>)}
      {s.status === "loaded" && canDispatch && (
        <Button size="sm" variant="outline" onClick={() => setCheckOpen(true)} data-testid={`shipment-checklist-${s.shipment_id}`}>
          Final Dispatch Verification {s.checklist_complete ? "✓" : ""}
        </Button>)}
      {s.status === "loaded" && canDispatch && s.checklist_complete && (
        <Button size="sm" className="bg-signal-green hover:bg-signal-green/90 text-white" disabled={confirming} onClick={() => dispatchNow()} data-testid={`shipment-dispatch-${s.shipment_id}`}>{confirming ? "CONFIRMING..." : "CONFIRM DISPATCH"}</Button>)}
      {s.status === "dispatching" && (
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground self-center" data-testid={`shipment-dispatching-${s.shipment_id}`}>Dispatch confirmation in progress…</span>)}
      {s.status === "dispatched" && canDispatch && !s.lr_number && (
        <Button size="sm" variant="outline" onClick={() => setLrOpen(true)} data-testid={`shipment-lr-${s.shipment_id}`}>Upload LR / Dispatch Proof</Button>)}
      {["dispatched", "in_transit", "delivered", "pod_received"].includes(s.status) && (canSales || canDispatch) && (
        <WhatsAppShare shipment={s} onChanged={onChanged} />)}
      {s.status === "dispatched" && s.lr_number && canSales && (
        <Button size="sm" variant="outline" onClick={() => setTransitOpen(true)} data-testid={`shipment-transit-${s.shipment_id}`}>Mark In Transit</Button>)}
      {s.status === "in_transit" && canSales && (
        <Button size="sm" variant="outline" onClick={() => setDeliverOpen(true)} data-testid={`shipment-deliver-${s.shipment_id}`}>Mark Delivered</Button>)}
      {["delivered", "pod_received"].includes(s.status) && (
        <span className="text-[10px] font-mono uppercase tracking-widest text-signal-green self-center">{s.status === "delivered" ? "Delivered — POD task with Sales" : "POD Received ✓"}</span>)}
      {s.status === "cancelled" && (
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground self-center" data-testid={`shipment-cancelled-${s.shipment_id}`}>Cancelled — {s.cancel_reason || "quantity released"}</span>)}

      <ChecklistDialog open={checkOpen} onOpenChange={setCheckOpen} shipment={s} onChanged={onChanged} />
      <Button size="sm" variant="outline" onClick={() => window.open(`${API}/shipments/${s.shipment_id}/dispatch-pack`, "_blank")} data-testid={`dispatch-pack-${s.shipment_id}`}>Dispatch Pack</Button>
      <LrDialog open={lrOpen} onOpenChange={setLrOpen} shipment={s} onChanged={onChanged} />
      <TransitDialog open={transitOpen} onOpenChange={setTransitOpen} shipment={s} onChanged={onChanged} />
      <DeliverDialog open={deliverOpen} onOpenChange={setDeliverOpen} shipment={s} onChanged={onChanged} />
      <CancelDialog open={cancelOpen} onOpenChange={setCancelOpen} shipment={s} onChanged={onChanged} />
    </div>
  );
}
